import React, { useState, useMemo } from 'react';
import { LineChart, XAxis, YAxis, CartesianGrid, Line, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from "recharts";
import { AnalyticsTooltip } from './common/AnalyticsTooltip';

const StatCard = ({ title, value, children }) => (
    <div className="bg-gray-50 p-4 rounded-lg shadow-inner">
        <h4 className="text-sm font-semibold font-nunito text-gray-600 mb-1">{title}</h4>
        {value && <p className="text-lg font-bold font-nunito text-gray-800">{value}</p>}
        <div className="font-nunito">{children}</div>
    </div>
);

export const AnalyticsDepotSelection = ({
    depot,
    onSelect,
    isSelected
}) => {
    const [showPopup, setShowPopup] = useState(false);

    // Use the depot data provided by the backend, with fallbacks if needed
    const depotName = depot.depot_name || `Depot ${depot.depot_id}`;
    const depotAddress = depot.depot_address || "Address information not available";

    const handleClick = () => {
        // Toggle selection - if already selected, unselect it by passing null
        onSelect(isSelected ? null : depot.depot_id);
    };

    const { chartData, nonZeroChargers, isActive } = useMemo(() => {
        const isActive = depot.is_active > 0.5;
        const nonZeroChargers = depot.chargers ? Object.entries(depot.chargers).filter(([_, count]) => count > 0) : [];
        
        const chargingScheduleData = Object.entries(depot.charging_schedule || {}).map(([timePeriod, periodData]) => {
            const timeIndex = parseInt(timePeriod, 10);
            const totalMinutes = timeIndex * 15;
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            const timeOfDay = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            return { timeOfDay, totalLoad: periodData.total_load || 0, electricityRate: periodData.electricity_rate || 0 };
        });

        return { chartData: { chargingScheduleData }, nonZeroChargers, isActive };
    }, [depot]);

    // Prepare charger data for chart
    const prepareChargerData = () => {
        if (!depot.chargers) return [];
        
        return Object.entries(depot.chargers).map(([chargerType, count]) => ({
            type: isNaN(chargerType) ? `${chargerType} kW` : `${parseInt(chargerType)} kW`,
            count: count
        }));
    };

    // Prepare daily cost data for chart
    const prepareCostData = () => {
        if (!depot.daily_costs) return [];
        
        return Object.entries(depot.daily_costs).map(([costType, amount]) => ({
            category: costType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            amount: amount
        }));
    };

    // Modify the prepareChargingScheduleData to include charging trucks information
    const prepareChargingScheduleData = () => {
        if (!depot) {
            console.log("Missing depot data");
            return [];
        }
        
        if (!depot.charging_schedule) {
            return [];
        }
        
        // Convert time period to time of day format (assuming 15-minute intervals)
        const formattedData = Object.entries(depot.charging_schedule).map(([timePeriod, periodData]) => {
            // Parse the time period as a number to calculate hours and minutes
            const timeIndex = parseInt(timePeriod, 10);
            // Calculate hours and minutes based on 15-minute intervals
            const totalMinutes = timeIndex * 15;
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            const timeOfDay = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            
            return {
                timePeriod,
                timeOfDay,
                totalLoad: periodData.total_load || 0,
                electricityRate: periodData.electricity_rate || 0,
                chargingTrucks: periodData.charging_trucks || []
            };
        });
        
        // Sort by time period to ensure chronological order
        formattedData.sort((a, b) => parseInt(a.timePeriod, 10) - parseInt(b.timePeriod, 10));
        
        return formattedData;
    };

    // Prepare solar data for chart
    const prepareSolarData = () => {
        if (!depot.solar || !depot.solar.generation_profile) return [];
        
        return Object.entries(depot.solar.generation_profile).map(([timePeriod, data]) => {
            // Parse the time period as a number to calculate hours and minutes
            const timeIndex = parseInt(timePeriod, 10);
            // Calculate hours and minutes based on 15-minute intervals
            const totalMinutes = timeIndex * 15;
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            const timeOfDay = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            
            return {
                timePeriod,
                timeOfDay,
                solarPower: data.power_kw || 0
            };
        }).sort((a, b) => parseInt(a.timePeriod, 10) - parseInt(b.timePeriod, 10));
    };

    // Prepare battery data for chart
    const prepareBatteryData = () => {
        if (!depot.battery || !depot.battery.operation_profile) return [];
        
        return Object.entries(depot.battery.operation_profile).map(([timePeriod, data]) => {
            // Parse the time period as a number to calculate hours and minutes
            const timeIndex = parseInt(timePeriod, 10);
            // Calculate hours and minutes based on 15-minute intervals
            const totalMinutes = timeIndex * 15;
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            const timeOfDay = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            
            return {
                timePeriod,
                timeOfDay,
                stateOfCharge: data.state_of_charge_kwh || 0,
                stateOfChargePercent: data.state_of_charge_percent || 0,
                chargingPower: data.charging_power_kw || 0,
                dischargingPower: data.discharging_power_kw || 0,
                netPower: (data.charging_power_kw || 0) - (data.discharging_power_kw || 0)
            };
        }).sort((a, b) => parseInt(a.timePeriod, 10) - parseInt(b.timePeriod, 10));
    };

    // Prepare grid interaction data for chart
    const prepareGridData = () => {
        if (!depot.grid_interaction || !depot.grid_interaction.profile) return [];
        
        return Object.entries(depot.grid_interaction.profile).map(([timePeriod, data]) => {
            // Parse the time period as a number to calculate hours and minutes
            const timeIndex = parseInt(timePeriod, 10);
            // Calculate hours and minutes based on 15-minute intervals
            const totalMinutes = timeIndex * 15;
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            const timeOfDay = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            
            return {
                timePeriod,
                timeOfDay,
                netPower: data.net_power_kw || 0,
                purchasedPower: data.power_purchased_kw || 0,
                soldPower: data.power_sold_kw || 0
            };
        }).sort((a, b) => parseInt(a.timePeriod, 10) - parseInt(b.timePeriod, 10));
    };

    // Format currency values
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(value);
    };

    // Updated to display circuit capacities alongside circuit IDs
    const renderConnectedCircuits = () => {
        if (!depot.connected_circuits || depot.connected_circuits.length === 0) {
            return "None";
        }
        
        return depot.connected_circuits.map((circuit, index) => {
            const capacity = depot.connected_circuit_capacities && 
                             depot.connected_circuit_capacities[index] ? 
                             `${depot.connected_circuit_capacities[index]} kW` : 
                             'N/A kW';
            
            return (
                <span key={`circuit-${index}`}>
                    {circuit} - SCE - {capacity}
                    {index < depot.connected_circuits.length - 1 ? ', ' : ''}
                </span>
            );
        });
    };

    const chargerData = prepareChargerData();
    const costData = prepareCostData();
    const chargingScheduleData = prepareChargingScheduleData();
    const solarData = prepareSolarData();
    const batteryData = prepareBatteryData();
    const gridData = prepareGridData();

    return (
        <>
            <button 
                className={`w-full p-4 border rounded-lg text-left transition-all duration-300 flex flex-col relative
                    ${isSelected ? "bg-blue-50 border-blue-500 shadow-md" : "bg-white hover:bg-gray-50 border-gray-200"}
                    ${isActive ? "border-l-4 border-l-green-500" : "border-l-4 border-l-red-500"}
                `}
                onClick={handleClick}
            >
                <span className="font-bold text-gray-800 text-lg mb-2 font-nunito">{depotName}</span>
                <div className="flex flex-col gap-1 text-sm text-gray-600">
                    <span className="flex items-center">
                        <span className="font-nunito">Address:</span> 
                        <span className="font-semibold ml-2 font-nunito">{depotAddress}</span>
                    </span>
                    <span className="flex items-center">
                        <span className="font-nunito">Status:</span> 
                        <span className={`font-semibold ml-2 font-nunito ${isActive ? 'text-green-600' : 'text-red-600'}`}>
                            {isActive ? "Active" : "Inactive"}
                        </span>
                    </span>
                    {isActive && nonZeroChargers.length > 0 && (
                        <span className="font-nunito"> Chargers: {nonZeroChargers.map(([type, count]) => `${count}x ${type}kW`).join(', ')}</span>
                    )}
                </div>
                <button 
                    className="mt-3 py-1 px-3 bg-blue-500 text-white text-xs font-semibold font-nunito rounded-md hover:bg-blue-600 self-start" 
                    onClick={(e) => { e.stopPropagation(); setShowPopup(true); }}
                >
                    Show Details
                </button>
            </button>

            {showPopup && (
                 <div 
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={() => setShowPopup(false)}
                >
                    <div 
                        className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-xl font-bold font-nunito text-gray-800">{`${depotName} Details`}</h3>
                            <button onClick={() => setShowPopup(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
                        </div>
                        <div className="p-6 overflow-y-auto space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                                <StatCard title="Status" value={isActive ? 'Active' : 'Inactive'} />
                                <StatCard title="Total Daily Cost" value={formatCurrency(depot.total_daily_cost || 0)} />
                                <StatCard title="Total Upfront Cost" value={formatCurrency(depot.total_upfront_cost || 0)} />
                                {depot.solar?.is_installed && <StatCard title="Solar Capacity" value={`${depot.solar.capacity_kw} kW`} />}
                                {depot.battery?.is_installed && <StatCard title="Battery Capacity" value={`${depot.battery.capacity_kwh} kWh`} />}
                            </div>
                            
                            {/* Grid Interaction Information - Moved above solar and battery */}
                            {depot.grid_interaction && (
                                <div className="p-4 border rounded-lg bg-gray-50">
                                    <h4 className="text-lg font-nunito font-semibold text-gray-700 mb-2">Grid Interaction</h4>
                                    <div className="renewable-spec font-nunito">
                                        <strong>Peak Power:</strong> {depot.peak_power?.toFixed(2)} kW
                                    </div>
                                    <div className="renewable-spec font-nunito">
                                        <strong>Connected Circuits:</strong> {renderConnectedCircuits()}
                                    </div>
                                    <div className="renewable-details font-nunito">
                                        <div className="renewable-spec">
                                            <strong>Daily Energy Purchased:</strong> {depot.grid_interaction.daily_energy_purchased_kwh?.toFixed(2)} kWh
                                            <span style={{ color: 'red', marginLeft: '5px' }}>←</span>
                                        </div>
                                        <div className="renewable-spec font-nunito">
                                            <strong>Daily Energy Sold:</strong> {depot.grid_interaction.daily_energy_sold_kwh?.toFixed(2)} kWh
                                            <span style={{ color: 'green', marginLeft: '5px' }}>→</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Solar System Information */}
                            {depot.solar && (
                                <div className="p-4 border rounded-lg bg-gray-50">
                                    <h4 className="text-lg font-semibold font-nunito text-gray-700 mb-2">Solar System</h4>
                                    <div className="renewable-status">
                                        <span className="status-indicator" style={{ 
                                            backgroundColor: depot.solar.is_installed ? '#4CAF50' : '#9E9E9E',
                                            display: 'inline-block',
                                            width: '12px',
                                            height: '12px',
                                            borderRadius: '50%',
                                            marginRight: '8px'
                                        }}></span>
                                        <span className="font-nunito"><strong> Status:</strong> {depot.solar.is_installed ? 
                                            'Recommended' : 'Not recommended'}
                                        </span>
                                    </div>
                                    {depot.solar.is_installed && (
                                        <div className="renewable-details">
                                            <div className="renewable-spec">
                                                <span className="font-nunito"><strong>Capacity:</strong> {depot.solar.capacity_kw?.toFixed(2)} kW</span>
                                            </div>
                                            <div className="renewable-spec">
                                                <span className="font-nunito"><strong>Daily Generation:</strong> {depot.solar.daily_generation_kwh?.toFixed(2)} kWh</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Battery System Information */}
                            {depot.battery && (
                                <div className="p-4 border rounded-lg bg-gray-50">
                                    <h4 className="text-lg font-semibold font-nunito text-gray-700 mb-2">Battery System</h4>
                                    <div className="renewable-status">
                                        <span className="status-indicator" style={{ 
                                            backgroundColor: depot.battery.is_installed ? '#4CAF50' : '#9E9E9E',
                                            display: 'inline-block',
                                            width: '12px',
                                            height: '12px',
                                            borderRadius: '50%',
                                            marginRight: '8px'
                                        }}></span>
                                        <span className="font-nunito"><strong>Status:</strong> {depot.battery.is_installed ? 
                                            'Recommended' : 'Not recommended'}
                                        </span>
                                    </div>
                                    {depot.battery.is_installed && (
                                        <div className="renewable-details">
                                            <div className="renewable-spec font-nunito">
                                                <strong>Capacity:</strong> {depot.battery.capacity_kwh?.toFixed(2)} kWh
                                            </div>
                                            <div className="renewable-spec font-nunito">
                                                <strong>Max Power:</strong> {depot.battery.max_power_kw?.toFixed(2)} kW
                                            </div>
                                            <div className="renewable-spec font-nunito">
                                                <strong>Daily Charge:</strong> {depot.battery.daily_charge_kwh?.toFixed(2)} kWh
                                                <span style={{ color: 'green', marginLeft: '5px' }}>↑</span>
                                            </div>
                                            <div className="renewable-spec font-nunito">
                                                <strong>Daily Discharge:</strong> {depot.battery.daily_discharge_kwh?.toFixed(2)} kWh
                                                <span style={{ color: 'red', marginLeft: '5px' }}>↓</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Charts */}
                            <div className="p-4 border rounded-lg bg-gray-50">
                                <h4 className="text-lg font-semibold font-nunito text-gray-700 mb-2">Charging Load Profile (kW)</h4>
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart data={chargingScheduleData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="timeOfDay" tick={{ fontSize: 10 }} />
                                        <YAxis tick={{ fontSize: 10 }} />
                                        <Tooltip content={(props) => <AnalyticsTooltip {...props} chartType="charging" />} />
                                        <Legend />
                                        <Line type="monotone" dataKey="totalLoad" name="Total Load" stroke="#8884d8" strokeWidth={2} dot={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Solar Generation Profile Chart */}
                            {depot.solar?.is_installed && solarData.length > 0 && (
                                <div className="p-4 border rounded-lg bg-gray-50">
                                    <h4 className="text-lg font-semibold font-nunito text-gray-700 mb-2">Solar Generation Profile (kW)</h4>
                                    <ResponsiveContainer width="100%" height={250}>
                                        <LineChart data={solarData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="timeOfDay" tick={{ fontSize: 10 }} />
                                            <YAxis tick={{ fontSize: 10 }} />
                                            <Tooltip content={(props) => <AnalyticsTooltip {...props} chartType="solar" />} />
                                            <Legend />
                                            <Line type="monotone" dataKey="solarPower" name="Solar Power" stroke="#FFD700" strokeWidth={2} dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Battery Operation Profile Chart */}
                            {depot.battery?.is_installed && batteryData.length > 0 && (
                                <div className="p-4 border rounded-lg bg-gray-50">
                                    <h4 className="text-lg font-semibold font-nunito text-gray-700 mb-2">Battery Storage Operation Profile</h4>
                                    <ResponsiveContainer width="100%" height={250}>
                                        <LineChart data={batteryData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="timeOfDay" tick={{ fontSize: 10 }} />
                                            <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                                            <Tooltip content={(props) => <AnalyticsTooltip {...props} chartType="battery" units={{
                                                stateOfChargePercent: '%',
                                                chargingPower: '',
                                                dischargingPower: 'kW'
                                            }} />} />
                                            <Legend />
                                            <Line yAxisId="left" type="monotone" dataKey="stateOfChargePercent" name="State of Charge (Left, %)" stroke="#4CAF50" strokeWidth={2} dot={false} />
                                            <Line yAxisId="right" type="monotone" dataKey="chargingPower" name="Charging Power (kW)" stroke="#2196F3" strokeWidth={2} dot={false} />
                                            <Line yAxisId="right" type="monotone" dataKey="dischargingPower" name="Discharging Power (kW)" stroke="#FF5722" strokeWidth={2} dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Grid Interaction Profile Chart */}
                            {depot.grid_interaction && gridData.length > 0 && (
                                <div className="p-4 border rounded-lg bg-gray-50">
                                    <h4 className="text-lg font-semibold font-nunito text-gray-700 mb-2">Grid Interaction Profile (kW)</h4>
                                    <ResponsiveContainer width="100%" height={250}>
                                        <LineChart data={gridData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="timeOfDay" tick={{ fontSize: 10 }} />
                                            <YAxis tick={{ fontSize: 10 }} />
                                            <Tooltip content={(props) => <AnalyticsTooltip {...props} chartType="grid" />} />
                                            <Legend />
                                            <Line type="monotone" dataKey="netPower" name="Net Load" stroke="#9C27B0" strokeWidth={2} dot={false} />
                                            {/* <Line type="monotone" dataKey="purchasedPower" name="Power Purchased" stroke="#FF9800" strokeWidth={2} dot={false} /> */}
                                            {/* <Line type="monotone" dataKey="soldPower" name="Power Sold" stroke="#4CAF50" strokeWidth={2} dot={false} /> */}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

